import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { notificationApi } from '../services/notificationApi';
import { Notification, NotificationCategory, FetchNotificationsOptions } from '../types/Notification';
import { useAuth } from './AuthContext';

interface UnreadCounts {
  total: number;
  leads?: number;
  tasks?: number;
  calls?: number;
  documents?: number;
  forms?: number;
  automation?: number;
  system?: number;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCounts: UnreadCounts;
  isLoading: boolean;
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
  selectedCategory: NotificationCategory | null;
  showUnreadOnly: boolean;
  fetchNotifications: (options?: FetchNotificationsOptions, append?: boolean) => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: (category?: NotificationCategory) => Promise<void>;
  archiveNotification: (id: number) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  setSelectedCategory: (category: NotificationCategory | null) => void;
  setShowUnreadOnly: (show: boolean) => void;
  loadMore: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({ total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<NotificationCategory | null>(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await notificationApi.getUnreadCount();
      setUnreadCounts({
        total: response.total || 0,
        leads: response.leads,
        tasks: response.tasks,
        calls: response.calls,
        documents: response.documents,
        forms: response.forms,
        automation: response.automation,
        system: response.system,
      });
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [isAuthenticated]);

  const fetchNotifications = useCallback(async (
    options: FetchNotificationsOptions = {},
    append: boolean = false
  ) => {
    if (!isAuthenticated) return;
    
    setIsLoading(true);
    try {
      const response = await notificationApi.getNotifications({
        page: options.page || 1,
        limit: options.limit || 20,
        category: options.category || selectedCategory,
        isRead: options.isRead !== undefined ? options.isRead : (showUnreadOnly ? false : null),
        isArchived: options.isArchived || false,
      });
      
      if (append) {
        setNotifications(prev => [...prev, ...response.notifications]);
      } else {
        setNotifications(response.notifications);
      }
      
      setCurrentPage(response.pagination.currentPage);
      setTotalPages(response.pagination.totalPages);
      setHasMore(response.pagination.hasNextPage);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, selectedCategory, showUnreadOnly]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;
    
    await fetchNotifications({ page: currentPage + 1 }, true);
  }, [hasMore, isLoading, currentPage, fetchNotifications]);

  const markAsRead = useCallback(async (id: number) => {
    try {
      await notificationApi.markAsRead(id);
      
      setNotifications(prev =>
        prev.map(n =>
          n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
        )
      );
      
      setUnreadCounts(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
      }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async (category?: NotificationCategory) => {
    try {
      await notificationApi.markAllAsRead(category);
      
      setNotifications(prev =>
        prev.map(n =>
          (!category || n.category === category)
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n
        )
      );
      
      await refreshUnreadCount();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [refreshUnreadCount]);

  const archiveNotification = useCallback(async (id: number) => {
    try {
      await notificationApi.archiveNotification(id);
      
      setNotifications(prev => prev.filter(n => n.id !== id));
      
      const notification = notifications.find(n => n.id === id);
      if (notification && !notification.isRead) {
        setUnreadCounts(prev => ({
          ...prev,
          total: Math.max(0, prev.total - 1),
        }));
      }
    } catch (error) {
      console.error('Error archiving notification:', error);
    }
  }, [notifications]);

  // Initial fetch when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchNotifications({ page: 1 });
      refreshUnreadCount();
    }
  }, [isAuthenticated, user, fetchNotifications, refreshUnreadCount]);

  // Poll for unread count every 30 seconds
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      refreshUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUnreadCount]);

  // Refetch when category or filter changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications({ page: 1 });
    }
  }, [selectedCategory, showUnreadOnly, isAuthenticated, fetchNotifications]);

  const value: NotificationContextType = {
    notifications,
    unreadCounts,
    isLoading,
    hasMore,
    currentPage,
    totalPages,
    selectedCategory,
    showUnreadOnly,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    refreshUnreadCount,
    setSelectedCategory,
    setShowUnreadOnly,
    loadMore,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
